/**
 * PhaseCompanyData — datos básicos de la persona moral.
 *
 * Visual hierarchy (Pro plan):
 *  - PRIMARY route: prominent gradient dropzone card at the top
 *    inviting the user to upload the CSF. The OCR pre-fill IS the
 *    Pro feature they pay for — make it impossible to miss.
 *  - SECONDARY route: manual capture, available via a clear button
 *    (not a tenue underlined link). When user chooses manual, the
 *    form shows below a small "captura manual" header and a real
 *    return banner offers a path back to OCR.
 *  - After upload: collapses to a compact "Documentos cargados ✓"
 *    confirmation with Reemplazar button.
 *
 * Plans without OCR: prominent card hidden, manual form is the
 * primary path; small upsell hint mentions Pro.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import useSubjectExtraction from "./useSubjectExtraction";
import useVigenciaRules from "./useVigenciaRules";
import composeFiscalAddress from "./composeFiscalAddress";
import PrefillPromptShell, { PrefillReturnBanner, OcrUpsellHint } from "./PrefillPromptShell";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PhaseCompanyData({ data, onNext, onBack, loading, draftId }) {
  const { ocrEnabled } = useVigenciaRules();

  const [legalName, setLegalName] = useState(data.legal_name || "");
  const [rfc, setRfc] = useState(data.rfc || "");
  const [businessAddress, setBusinessAddress] = useState(data.business_address || "");
  const [hasApoderado, setHasApoderado] = useState(!!data.has_apoderado);

  const [csfUploaded, setCsfUploaded] = useState(!!data.csf_empresa_uploaded);
  const [uploading, setUploading] = useState(false);
  // Two distinct states:
  //  - userChoseManual: the user explicitly clicked "capturar a mano".
  //    Sticky — we never override their choice on re-render.
  //  - Effective mode = "auto" when OCR is available AND user hasn't
  //    chosen manual; otherwise "manual". Derived, not stored.
  const [userChoseManual, setUserChoseManual] = useState(false);
  const mode = (ocrEnabled && !userChoseManual) ? "auto" : "manual";
  const [refreshKey, setRefreshKey] = useState(0);

  const extraction = useSubjectExtraction({
    draftId,
    subjectType: "company",
    subjectDraftId: null,
    enabled: csfUploaded,
    ocrEnabled,
    refreshKey,
  });

  useEffect(() => {
    if (!extraction.allDone) return;
    const m = extraction.mergedData || {};
    setLegalName((prev) => prev || m.legal_name || "");
    setRfc((prev) => prev || (m.rfc ? String(m.rfc).toUpperCase() : ""));
    setBusinessAddress((prev) => prev || composeFiscalAddress(m) || "");
    if (extraction.anyFailed) {
      toast.warning("No pudimos extraer algunos campos. Complétalos manualmente.");
    } else {
      toast.success("Datos de la empresa pre-llenados desde la CSF");
    }
  }, [extraction.allDone, extraction.anyFailed, extraction.mergedData]);

  async function handleCsfUpload(file) {
    if (!draftId || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", "csf_empresa");
      fd.append("subject_type", "company");
      fd.append("extract", "true");
      await axios.post(
        `${API}/api/customer-drafts/${draftId}/upload-document`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
      );
      setCsfUploaded(true);
      setRefreshKey((k) => k + 1);
      toast.success("CSF subida. Extrayendo datos…");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir la CSF");
    } finally {
      setUploading(false);
    }
  }

  const valid = legalName.trim() && rfc.trim() && businessAddress.trim();
  const continueBlocked = loading || !valid || extraction.isExtracting || uploading;

  // Drive the shell state from extraction lifecycle.
  let shellState = "idle";
  if (csfUploaded) {
    if (extraction.isExtracting) shellState = "extracting";
    else if (extraction.anyFailed) shellState = "failed";
    else if (extraction.allDone) shellState = "done";
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">Datos de la Empresa</h2>
        <p className="text-sm text-slate-400">
          Información identificadora de la persona moral.
        </p>
      </div>

      {/* ── PRIMARY route: prominent prefill card (Pro only) ──────── */}
      {ocrEnabled && mode === "auto" && (
        <PrefillPromptShell
          testIdPrefix="csf-empresa"
          state={shellState}
          elapsedSec={extraction.elapsedSec}
          title="Sube la CSF de la empresa y autocompletamos los datos"
          subtitle="Razón social, RFC y domicilio fiscal se leen automáticamente. Tú solo confirmas."
          manualLabel="Prefiero capturar los datos a mano"
          onManual={() => setUserChoseManual(true)}
          onReplace={() => { setCsfUploaded(false); }}
        >
          {!csfUploaded && (
            <>
              <input
                id="csf-empresa-input"
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => e.target.files[0] && handleCsfUpload(e.target.files[0])}
                className="hidden"
                disabled={uploading}
                data-testid="csf-empresa-input"
              />
              <label
                htmlFor="csf-empresa-input"
                data-testid="csf-empresa-label"
                className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-7 px-4 transition ${
                  uploading
                    ? "border-slate-700 bg-slate-900/30 cursor-not-allowed"
                    : "border-teal-500/40 bg-teal-500/5 hover:bg-teal-500/10 hover:border-teal-400/60 cursor-pointer"
                }`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin text-teal-300" />
                    <span className="text-sm text-slate-300">Subiendo…</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-7 h-7 text-teal-300" />
                    <span className="text-sm font-medium text-white">
                      Selecciona o arrastra la CSF aquí
                    </span>
                    <span className="text-xs text-slate-400">
                      PDF, JPG o PNG · máx. 10 MB
                    </span>
                  </>
                )}
              </label>
            </>
          )}
          {csfUploaded && shellState !== "done" && (
            <div className="text-xs text-slate-400">
              CSF recibida. Procesando con OCR…
            </div>
          )}
        </PrefillPromptShell>
      )}

      {/* ── Pro user chose manual: secondary return-to-OCR banner ── */}
      {ocrEnabled && mode === "manual" && !csfUploaded && (
        <PrefillReturnBanner
          testId="csf-empresa-return-banner"
          label="¿Tienes la CSF a la mano? Súbela y autocompletamos por ti."
          onReturn={() => setUserChoseManual(false)}
        />
      )}

      {/* ── Plan without OCR: subtle upsell ─────────────────────── */}
      {!ocrEnabled && (
        <OcrUpsellHint
          testId="ocr-disabled-hint"
          text="El pre-llenado automático de los datos de la empresa está disponible en"
        />
      )}

      {/* ── SECONDARY route: manual form, visually subordinate ────── */}
      <div>
        {/* Small subordinate header when the prominent prefill card
            is in the IDLE state — signals "or do it by hand below"
            without becoming co-protagonist. Hidden once the user is
            actively using OCR (done/extracting) since the form is
            already the consequence of the upload. */}
        {ocrEnabled && mode === "auto" && shellState === "idle" && (
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">
            o completa los datos manualmente
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label htmlFor="legal_name">Razón Social *</Label>
            <Input
              id="legal_name"
              data-testid="company-legal-name"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="Notaría 42 de la CDMX, S.C."
            />
          </div>

          <div>
            <Label htmlFor="rfc">RFC *</Label>
            <Input
              id="rfc"
              data-testid="company-rfc"
              value={rfc}
              onChange={(e) => setRfc(e.target.value.toUpperCase())}
              placeholder="NCD010203AB1"
              maxLength={13}
              className="uppercase"
            />
          </div>

          <div>
            <Label htmlFor="business_address">Domicilio de la Empresa *</Label>
            <Textarea
              id="business_address"
              data-testid="company-address"
              value={businessAddress}
              onChange={(e) => setBusinessAddress(e.target.value)}
              placeholder="Calle, número, colonia, ciudad, estado, CP, país"
              rows={3}
            />
          </div>

          <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
            <Label className="text-base">¿Existe Apoderado Legal? *</Label>
            <p className="text-xs text-slate-400 mb-3 mt-1">
              Si activas esta opción, te pediremos los datos y documentos del apoderado en una fase posterior.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                data-testid="company-has-apoderado-no"
                className={`flex-1 px-4 py-2 rounded-md border text-sm font-medium ${
                  !hasApoderado
                    ? "bg-teal-600 border-teal-500 text-white"
                    : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
                }`}
                onClick={() => setHasApoderado(false)}
              >
                No
              </button>
              <button
                type="button"
                data-testid="company-has-apoderado-yes"
                className={`flex-1 px-4 py-2 rounded-md border text-sm font-medium ${
                  hasApoderado
                    ? "bg-teal-600 border-teal-500 text-white"
                    : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
                }`}
                onClick={() => setHasApoderado(true)}
              >
                Sí
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} disabled={loading}>← Atrás</Button>
        <Button
          data-testid="company-data-continue-btn"
          disabled={continueBlocked}
          onClick={() => onNext({
            legal_name: legalName.trim(),
            rfc: rfc.trim().toUpperCase(),
            business_address: businessAddress.trim(),
            has_apoderado: hasApoderado,
            csf_empresa_uploaded: csfUploaded,
          })}
        >
          {extraction.isExtracting
            ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Esperando OCR…</>
            : loading ? "Guardando…" : "Continuar →"}
        </Button>
      </div>
    </div>
  );
}
